import { openDB } from "idb";
import { jwtDecode } from "jwt-decode";

const CACHE_TTL_MS = 12 * 60 * 60 * 1000;
const API_ORIGIN = process.env.REACT_APP_BASE_URL || "http://localhost:3001";
let databasePromise;

function getDatabase() {
    if (!databasePromise) {
        databasePromise = openDB("parkpilot.garageOffline.v1", 1, {
            upgrade(database) {
                database.createObjectStore("snapshots");
            },
        }).catch((error) => {
            databasePromise = null;
            throw error;
        });
    }
    return databasePromise;
}

export async function createGarageCacheScope(user, podiumId) {
    try {
        const token = localStorage.getItem("token");
        const allowed = user?.accessiblePodiums?.some((podium) => Number(podium.id) === Number(podiumId)) || Number(user?.podiumId) === Number(podiumId);
        if (!token || !user?.id || user.isTenantPortal || !podiumId || !allowed) return null;
        const expiresAt = Number(jwtDecode(token).exp) * 1000;
        if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) return null;
        const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));
        const sessionId = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
        return {
            key: `${API_ORIGIN}:${user.id}:${podiumId}:${sessionId}`,
            isCurrent: () => localStorage.getItem("token") === token && expiresAt > Date.now(),
        };
    } catch {
        return null;
    }
}

export async function saveGarageSnapshot(scope, rows) {
    if (!scope?.isCurrent()) return false;
    try {
        const database = await getDatabase();
        if (!scope.isCurrent()) return false;
        const savedAt = Date.now();
        const transaction = database.transaction("snapshots", "readwrite");
        let cursor = await transaction.store.openCursor();
        while (cursor) {
            if (savedAt - cursor.value.savedAt >= CACHE_TTL_MS) await cursor.delete();
            cursor = await cursor.continue();
        }
        const vehicles = rows
            .filter((row) => [1, 2, 3, 4, 5].includes(Number(row.vehicleStatusId)))
            .map((row) => ({
                vehicleId: row.vehicleId,
                vehicleTicketNum: row.vehicleTicketNum,
                vehicleMobile: row.vehicleMobile,
                vehicleMake: row.vehicleMake,
                vehicleColor: row.vehicleColor,
                vehicleStatusId: row.vehicleStatusId,
                parkingSpot: row.parkingSpot,
                hasPendingTransfer: row.hasPendingTransfer,
            }));
        if (!scope.isCurrent()) {
            transaction.abort();
            await transaction.done;
            return false;
        }
        await transaction.store.put({ savedAt, vehicles }, scope.key);
        await transaction.done;
        return true;
    } catch {
        return false;
    }
}

export async function readGarageSnapshot(scope) {
    if (!scope?.isCurrent()) return null;
    try {
        const database = await getDatabase();
        const snapshot = await database.get("snapshots", scope.key);
        if (!snapshot) return null;
        if (!Number.isFinite(snapshot.savedAt) || Date.now() - snapshot.savedAt >= CACHE_TTL_MS || snapshot.savedAt > Date.now()) {
            await database.delete("snapshots", scope.key);
            return null;
        }
        return scope.isCurrent() && Array.isArray(snapshot.vehicles) ? snapshot : null;
    } catch {
        return null;
    }
}

export async function removeGarageSnapshot(scope) {
    try {
        if (scope) await (await getDatabase()).delete("snapshots", scope.key);
    } catch {}
}

export async function clearGarageSnapshots() {
    try {
        await (await getDatabase()).clear("snapshots");
    } catch {}
}
